import { Action } from "redux";

import { ILanguageExtended } from "@multiweb/types/contracts";
import { TypedObject } from "@multiweb/types/react";
import { CLEAR_LOCALIZATION_ITEMS, CONCAT_LOCALIZATION_ITEMS, LOAD_LOCALIZATION_KEYS, LOCALIZATION_KEYS_LOADED, SET_LOCALE_CODE, SET_SUPPORTED_LANGUAGES } from "./constants";

/**
 * Option s lokalizovatelným názvem.
 * @template V Typ hodnoty, kterou option reprezentuje.
 */
export type LocalizableOption<V> = {
    readonly locKey: string;
    readonly value: V;
};

/**
 * Option s lokalizovaným názvem, který navíc obsahuje metadata v podobě generického typu.
 * @template V Typ hodnoty, kterou option reprezentuje.
 * @template T Typ hodnoty metadat
 */
export type ExtendedLocalizableOption<V, T> = {
    readonly meta?: T;
} & LocalizableOption<V>;

export type LocalizationItem = {
    readonly primaryText: string;
    readonly secondaryText?: string;
};

export interface Ii18nState {
    readonly localeCode: string;
    readonly supportedLanguages: readonly ILanguageExtended[];
    readonly localizationItems: {
        readonly [key: string]: LocalizationItem;
    };
}

/**
 * Obsahuje informace o spojených akcích pro načtení lokalizačních klíčů. Používá se pro batchování zpracování akcí.
 */
export type MergedLoadLocalizationActions = Readonly<{
    /** Unikátní klíče pro indikaci probíhajícího načítání */
    fetchingKeys: readonly string[];
    /** Unikátní lokalizační klíče, které se mají načíst */
    locKeys: readonly string[];
}>;

export interface ILoadLocalizationKeysAction extends Action<typeof LOAD_LOCALIZATION_KEYS> {
    readonly payload: {
        readonly fetchingKey: string;
        readonly localizationKeys: ReadonlyArray<string>;
        readonly enableBatching: boolean;
    };
}

export class ILoadLocalizationKeysAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_LOCALIZATION_KEYS;
    }
}

export interface ILocalizationKeysLoadedAction extends Action<typeof LOCALIZATION_KEYS_LOADED> {
    readonly payload: {
        readonly fetchingKey: string;
    };
}

export class ILocalizationKeysLoadedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOCALIZATION_KEYS_LOADED;
    }
}

export interface ISetLocaleCodeAction extends Action<typeof SET_LOCALE_CODE> {
    readonly payload: {
        readonly localeCode: string;
    };
}

export class ISetLocaleCodeAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_LOCALE_CODE;
    }
}

export interface ISetSupportedLanguagesAction extends Action<typeof SET_SUPPORTED_LANGUAGES> {
    readonly payload: {
        readonly supportedLanguages: readonly ILanguageExtended[];
    };
}

export class ISetSupportedLanguagesAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_SUPPORTED_LANGUAGES;
    }
}

export interface IClearLocalizationItemsAction extends Action<typeof CLEAR_LOCALIZATION_ITEMS> {
}

export class IClearLocalizationItemsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CLEAR_LOCALIZATION_ITEMS;
    }
}

export interface IConcatLocalizationItemsAction extends Action<typeof CONCAT_LOCALIZATION_ITEMS> {
    readonly payload: {
        readonly localizationItems: TypedObject<LocalizationItem>;
    };
}

export class IConcatLocalizationItemsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CONCAT_LOCALIZATION_ITEMS;
    }
}

export type GetTextSelectorParams = {
    readonly locKey: string;
    readonly params?: string[];
};
